<script lang="ts">
	// Sign-in methods (D20, UC-28): every way into the owner's account, and
	// adding or removing one. A second way in is how an owner gets back in
	// after losing the first.
	//
	// Google is added by a full-page redirect, as signing in is, and comes
	// back here with ?link= saying how it went. A password is added by a
	// mailed link (D18); nothing is added until it is opened. A removal is
	// undone, not confirmed, as every removal on the dashboard is.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import { formatDate } from '../format/time';
	import { passwordLengthOk } from '../owner/password-rules';
	import {
		addPassword,
		googleLinkUrl,
		linkOutcome,
		loadMethods,
		removeMethod,
		type SignInMethod,
	} from '../owner/methods';
	import { flash, removeWithUndo } from '../owner/notices.svelte';
	import { navigate } from '../router.svelte';

	interface Props {
		language: Language;
	}

	let { language }: Props = $props();

	let methods = $state<SignInMethod[] | null>(null);
	let failure = $state('');

	async function reload() {
		const loaded = await loadMethods();
		if (loaded) methods = loaded;
		else failure = message(language, 'loadFailed');
	}

	// What Google's round trip came back with, read once and taken off the
	// address bar, so a reload does not say it again.
	$effect(() => {
		const outcome = linkOutcome(window.location.search);
		if (outcome) window.history.replaceState(window.history.state, '', window.location.pathname);
		if (outcome === 'added') flash(message(language, 'methodsGoogleAdded'));
		else if (outcome === 'in-use') failure = message(language, 'methodsInUse');
		else if (outcome === 'email-taken') failure = message(language, 'methodsEmailTaken');
		else if (outcome === 'unfinished') failure = message(language, 'methodsUnfinished');
		void reload();
	});

	const hasGoogle = $derived(methods?.some((m) => m.provider === 'google') ?? false);
	const hasPassword = $derived(methods?.some((m) => m.provider === 'password') ?? false);
	const onlyOne = $derived(methods?.length === 1);

	let leaving = $state(false);
	function addGoogle() {
		leaving = true;
		window.location.assign(googleLinkUrl(language));
	}

	// The add-a-password form, open or not, and the address its link went to.
	let adding = $state(false);
	let email = $state('');
	let password = $state('');
	let errors = $state<{ email?: string; password?: string }>({});
	let working = $state(false);
	let sentTo = $state('');

	function openAdd() {
		adding = true;
		email = '';
		password = '';
		errors = {};
		failure = '';
	}

	async function add() {
		if (!email.trim() || !password || working) return;
		errors = {};
		failure = '';
		if (!passwordLengthOk(password)) {
			errors = { password: message(language, 'passwordRule') };
			return;
		}
		working = true;
		const result = await addPassword(email, password, language);
		working = false;
		switch (result.kind) {
			case 'sent':
				sentTo = result.email;
				adding = false;
				return;
			case 'weak':
				errors = { password: message(language, 'passwordRule') };
				return;
			case 'badEmail':
				errors = { email: message(language, 'createAccountBadEmail') };
				return;
			case 'emailTaken':
				errors = { email: message(language, 'methodsEmailTaken') };
				return;
			default:
				failure = result.message || message(language, 'loadFailed');
		}
	}

	function kind(m: SignInMethod): string {
		return message(language, m.provider === 'google' ? 'methodGoogle' : 'methodPassword');
	}

	function remove(m: SignInMethod) {
		if (!methods || methods.length < 2) return;
		const at = methods.indexOf(m);
		methods = methods.filter((x) => x.id !== m.id);
		failure = '';
		removeWithUndo(
			fill(message(language, 'methodsRemoved'), kind(m), m.email),
			async () => {
				const result = await removeMethod(m.id);
				if (result.kind === 'last') failure = message(language, 'methodsLast');
				else if (result.kind === 'failed') {
					failure = result.message || message(language, 'loadFailed');
				}
				if (result.kind !== 'removed') await reload();
			},
			() => {
				methods?.splice(at, 0, m);
			},
		);
	}
</script>

<div class="methods" data-testid="sign-in-methods">
	<div class="row">
		<Button
			testId="methods-back"
			label={message(language, 'ownerSettings')}
			tone="text-quiet"
			size="sm"
			onPress={() => navigate('/owner/settings')}
		/>
	</div>
	{#if methods === null && !failure}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'methodsLoading')}
				testId="methods-loading"
			/>
		</div>
	{:else}
		{#if failure}
			<Band tone="flag" testId="methods-failure">{failure}</Band>
		{/if}
		{#if methods}
			<Card title={message(language, 'methodsTitle')}>
				<div class="body">
					<div class="list">
						{#each methods as m (m.id)}
							<div class="method" data-testid={`method-${m.provider}`}>
								<span class="kind">{kind(m)}</span>
								<bdi class="email" dir="ltr">{m.email}</bdi>
								<span class="added">
									{fill(message(language, 'methodAdded'), formatDate(m.addedAt, language))}
								</span>
								<Button
									testId={`method-remove-${m.provider}`}
									label={message(language, 'methodRemove')}
									tone="text-quiet"
									size="sm"
									disabled={onlyOne}
									onPress={() => remove(m)}
								/>
							</div>
						{/each}
					</div>
					{#if onlyOne}
						<p class="line" data-testid="methods-last">{message(language, 'methodsLast')}</p>
					{/if}
					{#if sentTo}
						<p class="line" role="status" data-testid="methods-sent">
							<strong>{message(language, 'methodsSentBefore')}</strong>
							<bdi class="address" dir="ltr">{sentTo}</bdi>{message(language, 'methodsSentAfter')}
						</p>
					{/if}
					{#if adding}
						<div class="add">
							<div class="fields">
								<Field
									testId="methods-email"
									label={message(language, 'signInEmail')}
									type="email"
									autocomplete="email"
									mono
									maxLength={254}
									value={email}
									error={errors.email}
									errorTestId="methods-email-error"
									onInput={(value) => (email = value)}
								/>
								<Field
									testId="methods-password"
									label={message(language, 'signInPassword')}
									type="password"
									autocomplete="new-password"
									mono
									maxLength={128}
									value={password}
									note={message(language, 'passwordRule')}
									error={errors.password}
									errorTestId="methods-password-error"
									reveal={{
										show: message(language, 'passwordShow'),
										hide: message(language, 'passwordHide'),
										testId: 'methods-password-show',
									}}
									onInput={(value) => (password = value)}
								/>
							</div>
							<div class="actions">
								<Button
									testId="methods-add-submit"
									label={message(language, 'methodsAdd')}
									size="md"
									disabled={!email.trim() || !password}
									state={working ? 'pending' : 'idle'}
									pendingLabel={message(language, 'methodsAdding')}
									onPress={() => void add()}
								/>
								<Button
									testId="methods-add-cancel"
									label={message(language, 'cancel')}
									tone="text-quiet"
									size="sm"
									onPress={() => (adding = false)}
								/>
							</div>
						</div>
					{:else if !hasGoogle || (!hasPassword && !sentTo)}
						<div class="actions">
							{#if !hasGoogle}
								<Button
									testId="methods-add-google"
									label={message(language, 'methodsAddGoogle')}
									tone="secondary"
									size="sm"
									state={leaving ? 'pending' : 'idle'}
									pendingLabel={message(language, 'signInOpening')}
									onPress={addGoogle}
								/>
							{/if}
							{#if !hasPassword && !sentTo}
								<Button
									testId="methods-add-password"
									label={message(language, 'methodsAddPassword')}
									tone="secondary"
									size="sm"
									onPress={openAdd}
								/>
							{/if}
						</div>
					{/if}
				</div>
			</Card>
			<p class="foot">{message(language, 'methodsFoot')}</p>
		{/if}
	{/if}
</div>

<style>
	.methods {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.row {
		display: flex;
	}

	.wait {
		display: flex;
		justify-content: center;
		padding-block: var(--space-11);
	}

	.body {
		padding: 0 var(--card-pad) var(--card-pad);
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.list {
		display: flex;
		flex-direction: column;
	}

	.method {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-2) var(--space-6);
		padding-block: var(--space-5);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.kind {
		min-inline-size: 6em;
		font-weight: 700;
		color: var(--text-strong);
	}

	.email {
		flex: 1 1 14em;
		min-inline-size: 0;
		font-family: var(--font-mono);
		font-size: var(--text-sm);
		color: var(--text-body);
		overflow-wrap: anywhere;
	}

	.added {
		font-size: var(--text-2xs);
		color: var(--text-muted);
		white-space: nowrap;
	}

	.line {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.line strong {
		color: var(--text-strong);
	}

	.address {
		font-family: var(--font-mono);
		font-weight: 600;
		color: var(--text-strong);
	}

	.add {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		padding-block-start: var(--space-5);
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.fields {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
		gap: var(--space-7);
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3) var(--space-4);
	}

	.foot {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}
</style>
